import { useFileRecordRow } from "@/api/fileRecords";
import { PaneBackButton } from "@/components/PaneBackButton";
import {
  usePaneLocation,
  usePaneParams,
  usePaneRouterContextSafe,
} from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import { useRightPaneScrollRestore } from "@/pages/canvas/hooks/useRightPaneScrollRestore";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { AlertCircle, File, RefreshCw } from "lucide-react";
import { useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { FileRecordDetailPaneView } from "./FileRecordDetailPaneView";
import { LoadingOverlay } from "./LoadingOverlay";

/**
 * Route wrapper: loads `FileRecord` by id, then renders {@link FileRecordDetailPaneView}.
 *
 * Also accepts a pre-resolved `FileRecord` via `location.state.fileRecord`
 * (used for legacy `base: "image" | "video"` entities that don't have a real
 * file_record id — they are adapted inline at the navigation call site).
 *
 * Blocks open here too, rendered as a fitted stage (BlockContent) — the
 * lightbox on their node stays the full-size canvas surface, reached by
 * Expand or Enter. Blocks from before display metadata existed carry no
 * `display` and fall open as documents.
 */
export function FileRecordDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  const paneLocation = usePaneLocation("rightPane");
  const mainLocation = useLocation();

  // Accept a pre-resolved record from route state (legacy entity adapter path).
  const stateRecord: FileRecord | undefined =
    paneLocation?.state?.fileRecord ?? mainLocation.state?.fileRecord;

  const { fileRecordId } = usePaneParams(ROUTES.FILE_RECORDS.DETAIL);

  // The opener names the list it read the row from; the pane reads it there.
  const {
    record: fetchedRecord,
    isLoading,
    isError,
    isGone,
    refresh,
  } = useFileRecordRow(
    fileRecordId,
    paneLocation?.state?.scope ?? mainLocation.state?.scope,
  );
  const error = isError || isGone;

  // Prefer the fetched record once it lands; fall back to the
  // navigation-provided record for legacy entities whose id doesn't resolve
  // to a real file record. Not while the row is being read: the state copy
  // may carry links that have run out.
  const record = fetchedRecord ?? (isLoading ? undefined : stateRecord);

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  // Restore/persist this doc's scroll position per canvas. The `contents`
  // wrapper below adds no box, so layout is unchanged; the hook finds the pane's
  // scroll viewport within it. No-op outside a canvas or for full-bleed panes.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useRightPaneScrollRestore(scrollContainerRef, record?.id, !!record && !error);

  // Show the record whenever we have one (placeholder or fetched); a background
  // refetch error on an already-shown record shouldn't blank the pane. The
  // error UI below only renders when there's nothing to show.
  if (record) {
    return (
      <div ref={scrollContainerRef} className="contents">
        <FileRecordDetailPaneView
          record={record}
          showPaneClose={!!paneContext}
          onClose={handleClose}
        />
      </div>
    );
  }

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />
          <div className="flex-1" />
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>
      <PaneLayoutContent>
        {!fileRecordId && (
          <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
            <File className="h-12 w-12 text-muted-foreground" />
            <div className="text-muted-foreground text-center">
              <p className="font-medium">No file selected</p>
              <p className="text-sm">Pick a file to view it.</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleClose}>
              Close
            </Button>
          </div>
        )}

        {fileRecordId && isLoading && !record && <LoadingOverlay />}

        {fileRecordId && error && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
            <div className="p-4 rounded-full bg-destructive/10">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <div className="text-center">
              <p className="font-medium text-foreground">Failed to load file</p>
              <p className="text-sm text-muted-foreground mt-1">
                The file couldn&apos;t be retrieved. It may have been deleted or
                your session expired.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={isLoading}
              className="gap-2"
            >
              <RefreshCw
                className={cn("h-4 w-4", isLoading && "animate-spin")}
              />
              Retry
            </Button>
          </div>
        )}
      </PaneLayoutContent>
    </PaneLayout>
  );
}
